import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { getDailyQueue, reorderDailyQueue, type DailyQueueParams } from '../services/daily-queue.service';

function queueKey(params: DailyQueueParams) {
  return ['daily-queue', params.sportId, params.federationId ?? null, params.days ?? null] as const;
}

// The upcoming rotation for a federation+sport. `enabled` lets the page hold off until a
// super_admin has actually picked a sport (a federation_admin's own scope is always ready).
export function useDailyQueueQuery(params: DailyQueueParams, enabled = true) {
  return useQuery({
    queryKey: queueKey(params),
    queryFn: () => getDailyQueue(params),
    enabled: enabled && Boolean(params.sportId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// Save a reorder. On success, writes the response straight into the cache for this exact key
// (the mutation's own response IS the freshly reordered queue, so a second round-trip to refetch
// it would just ask the server to repeat what it already told us) rather than invalidating.
export function useReorderDailyQueueMutation(params: DailyQueueParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clipIds: string[]) => reorderDailyQueue(params.sportId, clipIds, params.federationId),
    onSuccess: (queue) => {
      qc.setQueryData(queueKey(params), queue);
    },
  });
}
